import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, MenuItem, FormControl } from "@mui/material";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { ReactComponent as Mode } from "../../../../assets/images/KnowledgeBase/mode.svg";
import { ReactComponent as Calendar } from "../../../../assets/images/KnowledgeBase/calender.svg";
import { ReactComponent as CreatedDate } from "../../../../assets/images/KnowledgeBase/created-date.svg";
import { ReactComponent as Editor } from "../../../../assets/images/KnowledgeBase/editor.svg";
import { ReactComponent as Author } from "../../../../assets/images/KnowledgeBase/author.svg";
import { ReactComponent as Category } from "../../../../assets/images/KnowledgeBase/category.svg";
import { ReactComponent as Visibility } from "../../../../assets/images/KnowledgeBase/visibility.svg";
import { ReactComponent as Comment } from "../../../../assets/images/KnowledgeBase/comments.svg"
import { ReactComponent as MenuIcon } from "../../../../assets/images/KnowledgeBase/menu-icon.svg";

import { getAllKbCategories } from "../../../../store/features/getCategoriesToKnowledgeBase";
import EmojiPicker from "emoji-picker-react";
import { ReactComponent as SmileEmoji } from "../../../../assets/images/ChatImages/smile.svg";
import useClickOutside from "../../../../hooks/useClickOutside";
import { createKbFeedback } from "../../../../store/features/createKbFeedback";
import { deleteKbFeedback } from "../../../../store/features/deleteFeedbackQueue";
import { toast } from "react-toastify";
import { buildExternalKbUrl, selectBusinessSlug } from "../../../../utils/kbPublicUrl";
import { fetchWorkspaceSettings } from "../../../../store/features/workspaceSettingsApi";

const ArticleSideBar = ({ articleData, setArticleData, onAddTag, isReadOnly = false, groupId }) => {
  const dispatch = useDispatch();
  const { data: categoriesData } = useSelector((state) => state.getAllKbCategories);
  const business_id = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const { data: userData } = useSelector((state) => state.loginToken);
  const user = useSelector((state) => state.loginToken?.response?.user);
  const businessSlug = useSelector(selectBusinessSlug);
  const publicArticleUrl = buildExternalKbUrl({
    slug: businessSlug,
    articleId: articleData._id,
    groupId,
  });

  const isExistingArticle = !!articleData._id;

  useEffect(() => {
    if (business_id && groupId) {
      dispatch(getAllKbCategories({ group_id: groupId }));
    }
  }, [dispatch, business_id, groupId]);

  useEffect(() => {
    if (business_id && !businessSlug) {
      dispatch(fetchWorkspaceSettings());
    }
  }, [dispatch, business_id, businessSlug]);

  const [newTag, setNewTag] = useState("");
  const [newComment, setNewComment] = useState("");
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [openMenuCommentId, setOpenMenuCommentId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);

  const commentMenuRef = useRef(null);
  useClickOutside(commentMenuRef, () => {
    if (openMenuCommentId) {
      setOpenMenuCommentId(null);
    }
  });

  const isEditor = user?.kbRole?.role === 'editor';

  const handleEmojiClick = (emojiObject) => {
    setNewComment((prev) => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  const commentInputRef = useRef(null);
  useClickOutside(commentInputRef, () => {
    if (showCommentInput) {
      setShowCommentInput(false);
      setShowEmojiPicker(false);
      if (editingCommentId) {
        setEditingCommentId(null);
        setNewComment("");
      }
    }
  });

  const handleChange = (field, value) => {
    setArticleData((prev) => ({ ...prev, [field]: value }));
  };




  const handleTagsUpdate = (selectedTags) => {

  };

  const addTag = () => {
    if (newTag.trim() && !articleData.tags.includes(newTag.trim())) {
      setArticleData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag("");
      setShowTagInput(false);
    }
  };

  const removeTag = (tagToRemove) => {
    setArticleData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const addComment = async () => {
    if (newComment.trim()) {
      if (isExistingArticle) {
        // Dispatch API action for existing article
        const payload = {
          business_id: business_id,
          articleId: articleData._id,
          comment: newComment.trim(),
          source: "internal",
          group_id: groupId,
        };

        try {
          const response = await dispatch(createKbFeedback(payload)).unwrap();
          toast.success("Comment added successfully");

          const feedbackData = response?.data || response;
          const createdComment = feedbackData?.comments?.[0];

          // Optimistically add comment to UI with real feedback/comment ids when available
          const comment = {
            id: createdComment?._id || Date.now().toString(),
            feedbackId: feedbackData?._id,
            author: user?.full_name || "Current User",
            avatar: user?.agent_image || "",
            user_id: user?._id,
            date: new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }),
            content: newComment.trim(),
          };
          setArticleData((prev) => ({
            ...prev,
            comments: [...prev.comments, comment],
          }));
          setNewComment("");
          setShowCommentInput(false);

        } catch (error) {
          toast.error(error?.message || "Failed to add comment");
        }

      } else {
        // Should not happen if UI is hidden, but keep logic safely or for purely local (which user said "do not allow")
        // User said "in new article do not allow to add comment". 
        // Previous logic was local-only. I will disable/alert or just do nothing if somehow triggered.
        toast.warn("Please save the article first to add comments.");
      }
    }
  };

  const handleEditComment = (comment) => {
    setNewComment(comment.content);
    setEditingCommentId(comment.id || comment._id);
    setShowCommentInput(true);
    setOpenMenuCommentId(null);
  };

  const handleDeleteComment = async (comment) => {
    const commentId = comment?.id || comment?._id;
    const feedbackId = comment?.feedbackId;

    setOpenMenuCommentId(null);

    if (!feedbackId) {
      toast.error("Unable to delete comment. Missing feedback id.");
      return;
    }

    try {
      await dispatch(deleteKbFeedback({ feedbackId, group_id: groupId })).unwrap();
      setArticleData((prev) => ({
        ...prev,
        comments: prev.comments.filter((c) => (c.id || c._id) !== commentId),
      }));
    } catch (error) {
      // Error toast is handled in deleteKbFeedback slice
    }
  };

  const handleUpdateComment = async () => {
    if (newComment.trim() && editingCommentId) {
      // Update comment in local state
      setArticleData((prev) => ({
        ...prev,
        comments: prev.comments.map((c) =>
          (c.id || c._id) === editingCommentId
            ? { ...c, content: newComment.trim() }
            : c
        ),
      }));
      setNewComment("");
      setEditingCommentId(null);
      setShowCommentInput(false);
      toast.success("Comment updated successfully");
    }
  };

  const customSelectSx = {
    height: "32px",
    width: "136px",
    fontSize: "14px",
    "& .MuiSelect-select": {
      fontSize: "14px",
      paddingLeft: "16px",
      paddingRight: "28px !important", // Make room for icon
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minHeight: "auto !important",
      display: "block",
      lineHeight: "32px",
      boxSizing: "border-box",
    },
    "& .MuiInputBase-input": {
      paddingLeft: "16px !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#D5DCE1 !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#D5DCE1 !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#D5DCE1 !important",
    },
    "& .MuiSelect-icon": {
      fontSize: "16px",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      right: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    },
    "& .MuiSelect-iconOpen": {
      fontSize: "16px",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%) rotate(180deg)",
      right: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        border: "1px solid #D5DCE1",
        marginTop: "4px",
        width: "120px",
        padding: " 0px 8px",
        borderRadius: "8px",
        "& .MuiMenuItem-root": {
          fontSize: "14px",
          padding: "8px 8px",
          color: "#405261",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          "&:hover": {
            color: "#061522",
            borderRadius: "8px",
            background: "#F9FAFA  !important",
          },
          "&.Mui-selected": {
            color: "#061522",
            borderRadius: "8px",
            background: "#F9FAFA !important",
          },
          "&.Mui-selected:hover": {
            background: "#F9FAFA  !important",
          },
        },
      },
    },
  };

  return (
    <div
      className={`Ticket-Main-SideBar Ticket-Main-SideBar--open`}
      style={{ width: "100%", height: "100%" }}
    >
      <div className="Team-SideBar">
        <p className="detail-text">Info</p>
      </div>

      <div className="AccountType-Main-Container01">
        {/* Mode */}
        <div className="AccountType-content">
          <div className="Account-Type01">
            <Mode />
            <p className="AccountType-text">Mode:</p>
          </div>
          <FormControl
            size="small"
            sx={{
              minWidth: "136px",
              "& .MuiInputBase-root": { height: "32px !important" },
            }}
          >
            <Select
              value={articleData.mode}
              onChange={(e) => handleChange("mode", e.target.value)}
              IconComponent={Dropdown}
              sx={customSelectSx}
              MenuProps={menuProps}
              disabled={isReadOnly}
            >
              <MenuItem value="edit">Edit View</MenuItem>
              <MenuItem value="preview">Preview</MenuItem>
              <MenuItem value="code">Code View</MenuItem>
            </Select>
          </FormControl>
        </div>

        {isExistingArticle && (
          <div className="AccountType-content">
            <div className="Account-Type01">
              <CreatedDate />
              <p className="AccountType-text">Created:</p>
            </div>
            <p className="AccountAgent-text">{articleData.createdDate}</p>
          </div>
        )}

        <div className="AccountType-content">
          <div className="Account-Type01">
            <Calendar />
            <p className="AccountType-text">Last Date Edit:</p>
          </div>
          <p className="AccountAgent-text">{articleData.lastDateEdit}</p>
        </div>

        {isExistingArticle && (
          <div className="AccountType-content">
            <div className="Account-Type01">
              <Editor />
              <p className="AccountType-text">Last Edit by:</p>
            </div>
            <p className="AccountAgent-text">{articleData.lastEditBy}</p>
          </div>
        )}

        {isExistingArticle && (
          <div className="AccountType-content">
            <div className="Account-Type01">
              <Author
                style={{ width: "20px", height: "20px", color: "#6b7280" }}
              />
              <p className="AccountType-text">Author:</p>
            </div>
            <p className="AccountAgent-text">{articleData.author}</p>
          </div>
        )}

        {(() => {
          const normalizeCategoryId = (value) => {
            if (value == null || value === "") return "";
            if (typeof value === "object") return String(value._id || value.id || "");
            return String(value);
          };

          const rawCats = Array.isArray(categoriesData)
            ? categoriesData
            : (Array.isArray(categoriesData?.data) ? categoriesData.data : []);

          // Dedupe list categories by id and name so "General" (or any
          // same-named row) cannot appear twice in the parent select.
          const cats = [];
          const knownIds = new Set();
          const knownNames = new Set();
          rawCats.forEach((c) => {
            const id = String(c?._id || c?.id || "");
            const nameKey = String(c?.name || "").trim().toLowerCase();
            if ((id && knownIds.has(id)) || (nameKey && knownNames.has(nameKey))) {
              return;
            }
            if (id) knownIds.add(id);
            if (nameKey) knownNames.add(nameKey);
            cats.push(c);
          });

          // Ensure categories already on the article (e.g. default "General")
          // appear in the select even if the categories list hasn't loaded yet.
          // Skip when the same id OR same name is already present — otherwise the
          // article's populated category duplicates list "General" under a
          // different/missing id match.
          (articleData.categoryIds || []).forEach((entry) => {
            if (entry && typeof entry === "object" && entry._id) {
              const id = String(entry._id);
              const nameKey = String(entry.name || "").trim().toLowerCase();
              if (knownIds.has(id) || (nameKey && knownNames.has(nameKey))) {
                return;
              }
              cats.push({ _id: entry._id, name: entry.name || "Category" });
              knownIds.add(id);
              if (nameKey) knownNames.add(nameKey);
            }
          });

          const findParentFromChildId = (childId) => {
            for (const parent of cats) {
              if (parent.children?.some((child) => String(child._id) === String(childId))) {
                return parent;
              }
            }
            return null;
          };

          const currentCategoryEntry = (articleData.categoryIds || [])[0];
          const currentCategoryId = normalizeCategoryId(currentCategoryEntry);
          const currentCategoryName = String(
            (currentCategoryEntry && typeof currentCategoryEntry === "object"
              ? currentCategoryEntry.name
              : "") || ""
          )
            .trim()
            .toLowerCase();

          let selectedParentId =
            cats.find((c) => String(c._id) === currentCategoryId)?._id || "";
          let selectedSubCategoryId = "";

          // Fall back to name match when article category id isn't in the list
          // (avoids empty selection after we skipped a duplicate-by-name merge).
          if (!selectedParentId && currentCategoryName) {
            selectedParentId =
              cats.find(
                (c) => String(c?.name || "").trim().toLowerCase() === currentCategoryName
              )?._id || "";
          }

          if (!selectedParentId && currentCategoryId) {
            const derivedParent = findParentFromChildId(currentCategoryId);
            if (derivedParent) {
              selectedParentId = derivedParent._id;
              selectedSubCategoryId = currentCategoryId;
            }
          }

          const selectedParentCategory = cats.find((c) => String(c._id) === String(selectedParentId));

          // If we have a parent selected but no sub-category yet, check if there's one in categoryIds
          if (selectedParentCategory?.children?.length && !selectedSubCategoryId) {
            selectedSubCategoryId = (articleData.categoryIds || [])
              .map(normalizeCategoryId)
              .find((id) =>
                selectedParentCategory.children.some((child) => String(child._id) === id)
              ) || "";
          }

          const handleParentChange = (parentId) => {
            if (parentId) {
              const cat = cats.find((c) => String(c._id) === String(parentId));
              setArticleData((prev) => ({
                ...prev,
                categoryIds: cat
                  ? [{ _id: cat._id, name: cat.name }]
                  : [parentId],
              }));
            } else {
              setArticleData((prev) => ({ ...prev, categoryIds: [] }));
            }
          };

          const handleSubCategoryChange = (subCategoryId) => {
            if (subCategoryId) {
              const subCat = selectedParentCategory?.children?.find(
                (c) => String(c._id) === String(subCategoryId)
              );
              setArticleData((prev) => ({
                ...prev,
                categoryIds: subCat
                  ? [{ _id: subCat._id, name: subCat.name }]
                  : [subCategoryId],
              }));
            } else if (selectedParentId) {
              const parentCat = cats.find((c) => String(c._id) === String(selectedParentId));
              setArticleData((prev) => ({
                ...prev,
                categoryIds: parentCat
                  ? [{ _id: parentCat._id, name: parentCat.name }]
                  : [selectedParentId],
              }));
            }
          };

          return (
            <>
              <div className="AccountType-content">
                <div className="Account-Type01">
                  <Category />
                  <p className="AccountType-text">Category:</p>
                </div>
                <FormControl
                  size="small"
                  sx={{
                    minWidth: "136px",
                    maxWidth: "136px",
                    "& .MuiInputBase-root": { height: "32px !important" },
                  }}
                >
                  <Select
                    value={selectedParentId}
                    onChange={(e) => handleParentChange(e.target.value)}
                    displayEmpty
                    IconComponent={Dropdown}
                    sx={customSelectSx}
                    MenuProps={menuProps}
                    disabled={isReadOnly}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: "#aaa" }}>Select</span>;
                      }
                      const cat = cats.find((c) => String(c._id) === String(selected));
                      return cat?.name || "Select";
                    }}
                  >
                    {cats.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {selectedParentCategory?.children?.length > 0 && (
                <div className="AccountType-content">
                  <div className="Account-Type01">
                    <Category />
                    <p className="AccountType-text">Sub-Category:</p>
                  </div>
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: "136px",
                      maxWidth: "136px",
                      "& .MuiInputBase-root": { height: "32px !important" },
                    }}
                  >
                    <Select
                      value={selectedSubCategoryId}
                      onChange={(e) => handleSubCategoryChange(e.target.value)}
                      displayEmpty
                      IconComponent={Dropdown}
                      sx={customSelectSx}
                      MenuProps={menuProps}
                      disabled={isReadOnly}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: "#aaa" }}>Select</span>;
                        }
                        const subCat = selectedParentCategory.children.find(
                          (c) => String(c._id) === String(selected)
                        );
                        return subCat?.name || "Select";
                      }}
                    >
                      {selectedParentCategory.children.map((c) => (
                        <MenuItem key={c._id} value={c._id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </>
          );
        })()}

        <div className="AccountType-content">
          <div className="Account-Type01">
            <Visibility />
            <p className="AccountType-text">Visibility:</p>
          </div>
          <FormControl
            size="small"
            sx={{
              minWidth: "136px",
              "& .MuiInputBase-root": { height: "32px !important" },
            }}
          >
            <Select
              value={articleData.visibility}
              onChange={(e) => handleChange("visibility", e.target.value)}
              IconComponent={Dropdown}
              sx={customSelectSx}
              MenuProps={menuProps}
              disabled={isReadOnly}
            >
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="internal">Internal</MenuItem>
            </Select>
          </FormControl>
        </div>

        {articleData._id && articleData.visibility?.toLowerCase() === 'public' &&
          !['pendingreview', 'draft', 'new'].includes(articleData.status?.toLowerCase()) && (
            <div className="AccountType-content">
              <div className="Account-Type01">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                <p className="AccountType-text">Public Link:</p>
              </div>
              <a
                href={publicArticleUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (!publicArticleUrl) e.preventDefault();
                }}
                style={{
                  color: '#148DFD',
                  fontSize: '14px',
                  fontWeight: 400,
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                View Article
              </a>
            </div>
          )}

        <div className="AccountType-content">
          <div className="Account-Type01">
            <Comment />
            <p className="AccountType-text">Comments:</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <div
              onClick={() => !isReadOnly && handleChange("disableComments", !articleData.disableComments)}
              style={{
                width: "36px",
                height: "20px",
                borderRadius: "10px",
                background: !articleData.disableComments ? "#148DFD" : "#E5E9EC",
                cursor: isReadOnly ? "not-allowed" : "pointer",
                position: "relative",
                transition: "background 0.2s ease",
                opacity: isReadOnly ? 0.6 : 1,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "16px",
                  background: "#FFF",
                  boxShadow: "0 2px 4px 0 rgba(6, 21, 34, 0.08)",
                  position: "absolute",
                  top: "2px",
                  left: !articleData.disableComments ? "18px" : "2px",
                  transition: "left 0.2s ease",
                }}
              />
            </div>
            <span
              style={{
                color: "#061522",
                fontFamily: "Inter",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              {!articleData.disableComments ? "Enabled" : "Disabled"}
            </span>
          </div>
        </div>

        <div className="tagSparator" />

        <div
          style={{ display: "flex", justifyContent: "space-between" }}
          className="AccountType-content"
        >
          <div className="Account-Type01">
            <p className="AccountType-text">Tags:</p>
          </div>
          {!isReadOnly && (
            <p
              className="SuperVisorChange"
              style={{ marginLeft: "2px" }}
              onClick={onAddTag}
            >
              +Add tags
            </p>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            paddingRight: "5px",
            justifyContent: "flex-start",
            marginBottom: "20px",
          }}
        >
          {articleData.tags.map((tag) => (
            <span
              key={tag._id || tag}
              className="TicketTag"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 999,
                border: "1px solid #CBD5DF",
                padding: "0px 16px",
                height: "32px",
                textTransform: "capitalize",
                fontWeight: 500,
              }}
            >
              {tag.name || tag}
              {!isReadOnly && (
                <span
                  style={{
                    marginLeft: 8,
                    marginBottom: "2px",
                    cursor: "pointer",
                    fontSize: 16,
                  }}
                  onClick={() => removeTag(tag)}
                >
                  ×
                </span>
              )}
            </span>
          ))}
        </div>

        {isExistingArticle && (
          <>
            <div className="tagSparator" />
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "15px",
                  marginTop: "10px",
                }}
              >
                <span style={
                  {
                    color: '#061522',
                    fontFamily: 'Inter',
                    fontSize: '18px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '18px',
                  }
                }>Comment</span>
                {!isReadOnly && (
                  <p
                    className="SuperVisorChange"
                    onClick={() => {
                      if (!showCommentInput) {
                        setEditingCommentId(null);
                        setNewComment("");
                      }
                      setShowCommentInput(!showCommentInput);
                    }}
                  >
                    + Add Comment
                  </p>
                )}
              </div>

              {showCommentInput && (
                <div style={{ marginBottom: "15px", position: "relative" }}>
                  <div
                    ref={commentInputRef}
                    style={{
                      width: "408px",
                      height: "160px",
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
                      display: "flex",
                      flexDirection: "column",
                      padding: "12px",
                      boxSizing: 'border-box',
                      position: "absolute",
                      zIndex: 100,
                      left: "50%",
                      transform: "translateX(-50%)",
                      top: "0"
                    }}
                  >
                    {showEmojiPicker && (
                      <div style={{ position: "absolute", bottom: "100%", left: 0, zIndex: 10 }}>
                        <EmojiPicker
                          onEmojiClick={handleEmojiClick}
                          width={300}
                          height={400}
                        />
                      </div>
                    )}
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write a comment..."
                      style={{
                        width: "100%",
                        flex: 1,
                        border: "none",
                        outline: "none",
                        resize: "none",
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontWeight: 400,
                        lineHeight: "22px",
                        color: "#061522",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "8px",
                      }}
                    >
                      <div style={{ cursor: "pointer" }} onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                        <SmileEmoji />
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={editingCommentId ? handleUpdateComment : addComment}
                          style={{
                            width: "104px",
                            height: "40px",
                            borderRadius: "8px",
                            background: "#148DFD",
                            boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                            color: "white",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: 500,
                          }}
                        >
                          {editingCommentId ? "Update" : "Comment"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {articleData.comments.map((comment) => (
                  <div
                    key={comment.id}
                    style={{
                      width: "368px",
                      borderRadius: "8px",
                      border: "1px solid #E5E9EC",
                      background: "#FFF",
                      padding: "24px",
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      gap: "24px",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "0",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            background: "#d1d5db",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          {comment.avatar ? (
                            <img
                              src={comment.avatar}
                              alt={comment.author}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                fontSize: "16px",
                                color: "#fff",
                                fontWeight: "bold",
                              }}
                            >
                              {comment.author
                                ? comment.author.charAt(0).toUpperCase()
                                : ""}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <p
                            style={{
                              color: "#061522",
                              fontFamily: "Inter",
                              fontSize: "14px",
                              fontStyle: "normal",
                              fontWeight: 500,
                              lineHeight: "18px",
                              margin: 0,
                              marginBottom: "2px",
                            }}
                          >
                            {comment.author}
                          </p>
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#405261",
                              margin: 0,
                              fontFamily: "Inter",
                              fontWeight: 400,
                              lineHeight: "16px",
                            }}
                          >
                            {comment.date}
                          </p>
                        </div>
                      </div>

                      {(isEditor || comment.user_id === user?._id) && (
                        <div style={{ position: "relative" }}>
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuCommentId(
                                openMenuCommentId === (comment.id || comment._id)
                                  ? null
                                  : (comment.id || comment._id)
                              );
                            }}
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <MenuIcon />
                          </div>

                          {openMenuCommentId === (comment.id || comment._id) && (
                            <div
                              ref={commentMenuRef}
                              style={{
                                position: "absolute",
                                right: 0,
                                top: "24px",
                                width: "136px",
                                height: "auto",
                                padding: "8px 0",
                                borderRadius: "8px",
                                border: "1px solid #D5DCE1",
                                background: "#FFF",
                                boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
                                zIndex: 10,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              {(comment.user_id === user?._id) && (
                                <div
                                  onClick={() => handleEditComment(comment)}
                                  style={{
                                    width: "120px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    paddingLeft: "8px",
                                    boxSizing: "border-box",
                                    cursor: "pointer",
                                    borderRadius: "8px",
                                    color: "#405261",
                                    fontFamily: "Inter",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "#F9FAFA";
                                    e.currentTarget.style.color = "#061522";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.color = "#405261";
                                  }}
                                >
                                  Edit
                                </div>
                              )}

                              {isEditor && (
                                <div
                                  onClick={() => handleDeleteComment(comment)}
                                  style={{
                                    width: "120px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    paddingLeft: "8px",
                                    boxSizing: "border-box",
                                    cursor: "pointer",
                                    borderRadius: "8px",
                                    // Base styles
                                    color: "#FF4D4F",
                                    fontFamily: "Inter",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "#F9FAFA";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                  }}
                                >
                                  Delete
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <p
                        style={{
                          color: "#405261",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontStyle: "normal",
                          fontWeight: 400,
                          lineHeight: "24px",
                          margin: 0,
                          wordBreak: "break-word",
                        }}
                      >
                        {comment.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div >
    </div >
  );
};

export default ArticleSideBar;
